import {
    Box,
    Button,
    Checkbox,
    Flex,
    FormControl,
    Heading,
    IconButton,
    Input,
    InputGroup,
    InputRightElement,
    Stack,
    Text,
    useColorModeValue,
    Image,
  } from "@chakra-ui/react";
  import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
  import { Formik, Form, Field, ErrorMessage } from "formik";
  import * as Yup from "yup";
  import { useDispatch, useSelector } from "react-redux";
  import { useNavigate } from "react-router-dom";
  import { ToastChakra } from "../../helpers/toast";
  import { login, reset } from "../../features/authSlice";
  import { useEffect, useState } from "react";
  import logo from "../../assets/img/green-technology.png";
  import bgGradient from "../../assets/img/gradient-bg.svg";
  
  const Login = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const savedRemember = localStorage.getItem("remember") === "true";
    const savedCorreo = localStorage.getItem("correo");
    const savedPassword = localStorage.getItem("password");
    // Si se recuerda el correo, precargamos el campo
  
    const initialValues = {
      correo: savedCorreo,
      password: savedPassword,
      remember: savedRemember,
    };
  
    const validationSchema = Yup.object({
      correo: Yup.string()
        .email("Ingrese un correo válido")
        .required("El correo es requerido"),
      password: Yup.string().required("La contraseña es requerida"),
    });
  
    const [showPassword, setShowPassword] = useState(false);
    const handleShowClick = () => setShowPassword(!showPassword);
  
    const bgCard = useColorModeValue("white", "gray.800");
    const inputBg = useColorModeValue("gray.50", "gray.700");
    const inputBorder = useColorModeValue("gray.200", "gray.600");
    const inputFocusBorder = useColorModeValue("blue.500", "blue.300");
  
    const { isLoading, isError, isSuccess, message } = useSelector(
      (state) => state.auth
    );
  
    useEffect(() => {
      if (isError) {
        ToastChakra("Mensaje", message, "error", 2500, "bottom");
      }
  
      dispatch(reset());
    }, [dispatch, isSuccess, isError, message, navigate]);
  
    const handleLogin = (values, { setSubmitting }) => {
      if (values.remember) {
        localStorage.setItem("remember", "true");
        localStorage.setItem("correo", values.correo);
        localStorage.setItem("password", values.password);
      } else {
        localStorage.removeItem("remember");
        localStorage.removeItem("correo");
        localStorage.removeItem("password");
      }
  
      const userData = {
        correo: values.correo,
        password: values.password,
      };
  
      dispatch(login(userData))
        .then((action) => {
          if (action.payload) {
            localStorage.setItem("user", JSON.stringify(action.payload));
            localStorage.setItem("rol", action.payload.usuario.rol.key);
  
            if (action.payload.usuario?.cambiar_password) {
              navigate("/cambiar-password");
            } else {
              navigate("/");
              ToastChakra(
                "BIENVENIDO A LA PLATAFORMA - SIGNIA-SERVICIOS",
                "CARGANDO LOS DATOS ...",
                "loading",
                3500
              );
            }
          } else {
            console.error("No se recibieron datos del usuario.");
          }
        })
        .catch((error) => {
          console.error("Error al iniciar sesión:", error);
        })
        .finally(() => {
          setSubmitting(false);
        });
    };
  
    return (
      <Flex
        align="center"
        justify="center"
        minHeight="100vh"
        bgImage={bgGradient}
        bgSize="cover"
        bgPosition="center"
        bgRepeat="no-repeat"
        p={4}
      >
        <Box
          w={{ base: "100%", sm: "90%", md: "800px" }}
          maxW="100%"
          display="flex"
          flexDirection={{ base: "column", md: "row" }}
          boxShadow="xl"
          borderRadius="lg"
          overflow="hidden"
          bg={bgCard}
        >
          {/* Panel Izquierdo (branding) - solo visible desde md hacia arriba */}
          <Flex
            w={{ base: "100%", md: "40%" }}
            bg="blue.600"
            color="white"
            align="center"
            justify="center"
            direction="column"
            p={6}
            display={{ base: "none", md: "flex" }}
          >
            <Image
              src={logo}
              alt="Logo"
              maxW="150px"
              mb={4}
              objectFit="cover"
            />
            <Text fontSize="xl" fontWeight="bold" mb={2}>
              SIGNIA
            </Text>
            <Text fontSize="xs" fontWeight="bold" mb={1} textAlign="center">
              BIENVENIDO A TU PLATAFORMA DE GESTIÓN
            </Text>
            <Text
              fontSize="sm"
              fontStyle="italic"
              fontWeight="medium"
              color="white"
              mb={4}
              textAlign="center"
            >
              "Tu aliado en la gestión de cada día"
            </Text>
          </Flex>
  
          {/* Panel de formulario */}
          <Box w={{ base: "100%", md: "60%" }} p={8}>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleLogin}
              enableReinitialize
            >
              {({ values, handleChange, isSubmitting }) => (
                <Form>
                  <Stack spacing={6}>
                    <Box textAlign="center">
                        {/* Logo para vista móvil */}
                    <Flex
                        display={{ base: "flex", md: "none" }}
                        justify="center"
                        align="center"
                        direction="column"
                        mb={4}
                    >
                    <Image src={logo} alt="Logo" maxW="100px" mb={2} />
                    <Text fontSize="lg" fontWeight="bold" color="blue.600">
                        SIGNIA
                    </Text>
                    </Flex>
                      <Heading size="lg" fontWeight="bold">
                        Iniciar sesión
                      </Heading>
                      <Text fontSize="sm" color="gray.500">
                        Ingresa tus credenciales para acceder
                      </Text>
                    </Box>
  
                    <Stack spacing={4}>
                      <Field name="correo">
                        {({ field, form }) => (
                          <FormControl
                            isInvalid={form.errors.correo && form.touched.correo}
                          >
                            <Input
                              {...field}
                              type="email"
                              placeholder="Correo electrónico"
                              bg={inputBg}
                              borderColor={inputBorder}
                              _focus={{ borderColor: inputFocusBorder }}
                            />
                            <ErrorMessage
                              name="correo"
                              component={Text}
                              color="red.500"
                              fontSize="sm"
                            />
                          </FormControl>
                        )}
                      </Field>
  
                      <Field name="password">
                        {({ field, form }) => (
                          <FormControl
                            isInvalid={
                              form.errors.password && form.touched.password
                            }
                          >
                            <InputGroup>
                              <Input
                                {...field}
                                type={showPassword ? "text" : "password"}
                                placeholder="Contraseña"
                                bg={inputBg}
                                borderColor={inputBorder}
                                _focus={{ borderColor: inputFocusBorder }}
                                autoComplete="current-password" // Permite que el navegador sugiera contraseñas
                              />
                              <InputRightElement>
                                <IconButton
                                  variant="ghost"
                                  size="sm"
                                  aria-label="Mostrar contraseña"
                                  icon={
                                    showPassword ? <ViewIcon /> : <ViewOffIcon />
                                  }
                                  onClick={handleShowClick}
                                />
                              </InputRightElement>
                            </InputGroup>
                            <ErrorMessage
                              name="password"
                              component={Text}
                              color="red.500"
                              fontSize="sm"
                            />
                          </FormControl>
                        )}
                      </Field>
                    </Stack>
  
                    <Flex justify="space-between" align="center">
                      <Checkbox
                        name="remember"
                        onChange={handleChange}
                        isChecked={values.remember}
                      >
                        Recordarme
                      </Checkbox>
                    </Flex>
  
                    <Button
                      type="submit"
                      bg="blue.600"
                      color="white"
                      _hover={{ bg: "blue.700" }}
                      _active={{ bg: "blue.800" }}
                      isLoading={isLoading}
                      disabled={isSubmitting}
                      loadingText="Ingresando..."
                      size="md"
                      borderRadius="md"
                      spinnerPlacement="end"
                    >
                      Iniciar sesión
                    </Button>
                  </Stack>
                </Form>
              )}
            </Formik>
          </Box>
        </Box>
      </Flex>
    );
  };
  
  export default Login;
  